
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, forkJoin } from 'rxjs';
import { map, switchMap} from 'rxjs/operators';
import { promiseOnlyResolved } from '@Core/utils';

import { TargetingFacetCollection, BaseSearchService } from '../../../services/base-search.service';
import { TargetingListsApiService } from '../../../../../api/targeting/targeting-lists.api.service';
import { JournodexSubjectsService } from '../../../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../../../Core/services/journodex-enums.service';
import { JournodexRegionsService } from '../../../../../Common/Journodex/services/journodex-regions.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { TargetingResultFactory } from '../../../factories/targeting-result.factory';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { DiscoverRoutingService } from '../../../../DiscoverRouter/services/discover-routing.service';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { RecipientNegativeoutcomeSearchModel } from '../../../models/recipients-negativeoutcome-search.model';
import { FilterTypeModel } from '../../../../../Common/Filter/models/abstract-filter-type.model';
import { TargetingSearch } from '../../../models/targeting-search.model';
import { TargetingSearchResponseApiModel } from '../../../../../api/targeting/model/targeting-search-response.api.model';
import { RecipientSearchResultsApiModel } from '../../../../../api/distributions/model/recipient-search-results.api.model';
import { DistributionReportStat } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { DistributionApi } from '../../../../../api/distributions/distribution.api';
import { topStatLabels } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-map.model';
import { EntityIdApiModel } from '../../../../../api/targeting/model/entity-id-api-model';
import { IncludeExcludeModel } from '../../../../../Common/Misc/models/include-exclude-model';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { ListMembershipApiModel } from '../../../../../api/targeting/model/list-membership-api-model';
import { TargetingListService } from '../../../services/targeting-list.service';
import { TargetingListApi } from '../../../../../api/targeting/model/targeting-list-api.model';
import { TargetingSearchesApiService } from '../../../../../api/targeting/targeting-searches.api.service';
import { RecipientNegativeoutcomeStatsModel } from '../../../models/recipients-negativeoutcome-stats.model';
import { FilterOptionModel } from '../../../../../Common/Filter/models/filter-option.model';
import { FilterSectionEnum } from '../../../../../Common/Filter/enums/filter-section.enum';

@Injectable()
export class NegativeRecipientSearchService extends BaseSearchService implements OnDestroy {

    readonly deliveryStatusEnum: DistributionReportStat[] = [
        DistributionReportStat.UNSUBSCRIBED,
        DistributionReportStat.PREVIOUS_UNSUB,
        DistributionReportStat.HARD_BOUNCED,
        DistributionReportStat.PREVIOUS_HARD_BOUNCED,
        DistributionReportStat.SPAM_REPORT
    ];

    distributionId: number;
    targetingListIds: number[];
    allRecipientLists: TargetingListApi[];

    recipientSearch: RecipientNegativeoutcomeSearchModel = new RecipientNegativeoutcomeSearchModel();

    private negativeOutcomeListStats = new BehaviorSubject<RecipientNegativeoutcomeStatsModel>(new RecipientNegativeoutcomeStatsModel());
    negativeOutcomeListStats$: Observable<RecipientNegativeoutcomeStatsModel> = this.negativeOutcomeListStats.asObservable();

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected journodexSubjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected discoverRoutingService: DiscoverRoutingService,
        protected userPreferencesService: UserPreferencesService,
        protected distributionApiService: DistributionApi,
        protected targetingListService: TargetingListService
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            journodexSubjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );

        this.addDeliveryStatusFilterOptions();

        this.subscriptions.push(
            combineLatest([
                this.discoverRoutingService.getCurrentRoute(),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map (userPreference => userPreference.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings)
                ),
                this.targetingListService.all$
            ]).subscribe(([route, userPreference, targetingLists]) => {
                this.distributionId = +route.value;
                this.pageCriteria.pageSize = userPreference.pageSize;
                this.setPageCriteria(this.pageCriteria, false);
                if(this.distributionId && targetingLists) {
                    this.targetingListIds = targetingLists.map(i => i.id);
                    this.reloadLists();
                }
            }),
            this.targetingListService.getListMembershipsChanged().subscribe((event: any) => {
                this.reloadLists();
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    getFilterDefinitions(): FilterTypeModel[] {
        const filters = [
            this.contactNameFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.locationFilter,
            this.deliveryStatusFilter,
            this.listFilter,
            this.outletNameFilter
        ];

        this.outletNameFilter.section = FilterSectionEnum.CONTACT_DETAIL;

        return filters;
    }

    getAllRecipientLists(): TargetingListApi[] {
        return this.allRecipientLists;
    }

    addListFilterOptions(lists: TargetingListApi[]): void {
        const options = lists?.filter(list => list.name)?.map(list => ({
            label: list.name,
            value: list.id
        }));
        this.listFilter.options = options;
    }

    addDeliveryStatusFilterOptions(stats = new RecipientNegativeoutcomeStatsModel()): void {
        const options: FilterOptionModel<string>[] = [];
        for(const stat in stats){
            if(stats.hasOwnProperty(stat)){
                options.push({
                    label: topStatLabels.get(stat as DistributionReportStat),
                    value: stat,
                    count: stats[stat]
                });
            }
        }
        this.deliveryStatusFilter.options = options;
    }

    // This function is mainly used to unsubscribe "search requests" when they are not used anymore.
    private subscribe(prop: string, subscription: Observable<any>, handler: Function): void {
        if(this[prop]){
            this[prop].unsubscribe();
        }
        this[prop] = subscription.subscribe({
            next: (res) => handler(res),
            error: (err) => console.log(err)
        });
    }

    public resetSearch() {
        this.clearFilters();
        this.doSearch(true, true);
    }

    public doSearch(refreshPrimaryFacets: boolean = false, refreshFilteredFacets: boolean = false): Observable<TargetingSearchResponseApiModel> {
        const search = new TargetingSearch();
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();
        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }
        const deliveryStatusFilterValue = this.deliveryStatusFilter.getValue();
        const deliveryStatus = deliveryStatusFilterValue.length > 0 ? deliveryStatusFilterValue : this.deliveryStatusEnum;
        this.recipientSearch.deliveryStatus = IncludeExcludeModel.fromArray(deliveryStatus);

        const mainSearchRequest: Observable<any> = this.fastSearch(search, filters);
        this.subscribe('mainSearchRequest', mainSearchRequest, () => this.fetchListMemberships());

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            filteredFacetRequest = this.facetSearch(search, filters);
            this.subscribe('filteredFacedRequest', filteredFacetRequest, (collection: TargetingFacetCollection) => this.setFilteredFacets(collection));
        }

        if (refreshPrimaryFacets) {
            const primaryFacetRequest: Observable<TargetingFacetCollection> = isFiltered || !filteredFacetRequest
                ? this.facetSearch(search)
                : filteredFacetRequest;
            this.subscribe('primaryFacetRequest', primaryFacetRequest, (collection: TargetingFacetCollection) => this.setPrimaryFacets(collection));
        }

        return mainSearchRequest;
    }

    fastSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<RecipientSearchResultsApiModel> {
        this.recipientSearch.query = search.toApiModel(false, filters).query;
        const rows = this.pageCriteria.pageSize;
        const start = (this.pageCriteria.page - 1) * rows;

        // To Do: Loading
        // this.searchLoading$.next(true);
        const searchRequest: Observable<RecipientSearchResultsApiModel> = this.distributionApiService.getNegativeOutcomeRecipients(
            this.distributionId,
            this.recipientSearch,
            start,
            rows
        );

        return searchRequest.pipe(
            switchMap(async response => {
                this.nResults = response.numResults;
                this.nResults$.next(this.nResults);
                const resultPromises = response.recipients.map(x => this.targetingResultFactory.createRecipientResult(x));
                this.results = await promiseOnlyResolved(resultPromises);
                return response;
            })
        );
    }

    facetSearch(search: TargetingSearch, filters?: TargetingSearch): Observable<TargetingFacetCollection> {
        this.recipientSearch.query = search.toApiModel(true, filters).query;
        const rows = 0;
        const start = 0;

        const searchRequest: Observable<RecipientSearchResultsApiModel> = this.distributionApiService.getNegativeOutcomeRecipients(
            this.distributionId,
            this.recipientSearch,
            start,
            rows
        );

        return searchRequest.pipe(map(response => {
            return this.parseFacets(response?.facetResults || []);
        }));
    }

    applyListMembershipsToResult(result: TargetingResultModel, listMemberships: ListMembershipApiModel): void {
        super.applyListMembershipsToResult(result, listMemberships);
        if(result.listMemberships) {
            result.listMemberships = result.listMemberships.filter(item => this.targetingListIds.includes(item.id));
            result.listMembershipChips = result.listMemberships.map(item => ({
                label: item.name,
                tooltip: item.name,
                id: item.id
            }));
        }
    }

    getNegativeOutcomeLists(): Observable<{
        status: DistributionReportStat;
        lists: TargetingListApi[];
    }[]> {
        const apiCalls = this.deliveryStatusEnum.map(status =>
            this.distributionApiService.getNegativeOutcomeLists(
                this.distributionId,
                {
                    deliveryStatus: IncludeExcludeModel.fromArray([status]),
                    query: new TargetingSearch().toApiModel(false).query,
                }
            ).pipe(
                map(lists => ({
                    status,
                    lists: lists.filter(list => this.targetingListIds.includes(list.id))
                }))
            )
        );
        return forkJoin(apiCalls);
    }

    reloadLists(): void {
        if(!this.distributionId || !this.targetingListIds) {
            return;
        }
        const negativeOutcomeStatsRequest = this.getNegativeOutcomeLists();
        this.subscribe(
            'negativeOutcomeListsRequest',
            negativeOutcomeStatsRequest,
            (results: {
                status: DistributionReportStat;
                lists: TargetingListApi[];
            }[]) => {
                const negativeOutcomeStats = new RecipientNegativeoutcomeStatsModel();

                for(const result of results){
                    negativeOutcomeStats[result.status] = result.lists?.length || 0;
                }
                this.negativeOutcomeListStats.next(negativeOutcomeStats);
                const uniqueLists = Array.from(
                    results.flatMap(item => item.lists).reduce((res, item) => res.set(item.id, item), new Map()).values()
                );
                this.allRecipientLists = uniqueLists;
                this.addListFilterOptions(uniqueLists);
            }
        );
        this.doSearch(true, true);
    }

    removeSelectedRecipientsFromLists(entities: EntityIdApiModel[], listIds: number[]): Observable<void> {
        return this.targetingListsApiService.removeEntitiesFromLists(entities, listIds);
    }

    removeAllRecipientsFromLists(listIds: number[]): Observable<void> {
        let filters: TargetingSearch;
        if (this.hasFiltersToApply()) {
            filters = this.generateFiltersSearch();
        }
        const deliveryStatusFilterValue = this.deliveryStatusFilter.getValue();
        return this.distributionApiService.removeNegativeOutcomeRecipientsFromLists(
            this.distributionId,
            {
                deliveryStatus: IncludeExcludeModel.fromArray(
                    deliveryStatusFilterValue.length > 0 ? deliveryStatusFilterValue : this.deliveryStatusEnum
                ),
                query: new TargetingSearch().toApiModel(false, filters).query
            },
            listIds
        );
    }
}
